import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { CurrentClientUser } from '@client/entity/current-client-user/domain/model/current-client-user.model';
import { CurrentClientUserClient } from '@client/entity/current-client-user/domain/model/current-client-user-client.model';
import { NoSelectedClientIdError } from '@client/entity/client/domain/error/no-selected-client-id.error';

export const useCurrentClientUserStore = defineStore('current-client-user', () => {
  const currentClientUser = ref<CurrentClientUser | null>(null);
  const selectedClient = ref<CurrentClientUserClient | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  function setCurrentClientUser(user: CurrentClientUser | null): void {
    currentClientUser.value = user;
  }

  function setSelectedClient(client: CurrentClientUserClient): void {
    selectedClient.value = client;
  }

  function clearSelectedClient(): void {
    selectedClient.value = null;
  }

  function setLoading(loading: boolean): void {
    isLoading.value = loading;
  }

  function reset(): void {
    currentClientUser.value = null;
    selectedClient.value = null;
    isLoading.value = false;
    error.value = null;
  }

  function setError(message: string | null): void {
    error.value = message;
  }

  const definedCurrentClientUser = computed<CurrentClientUser>(() => {
    if (currentClientUser.value === null) {
      throw new Error('current user store - try to access current user but is is not defined');
    }

    return currentClientUser.value;
  });

  const definedSelectedClient = computed<CurrentClientUserClient>(() => {
    if (!selectedClient.value) {
      throw new NoSelectedClientIdError();
    }

    return selectedClient.value;
  });

  return {
    currentClientUser,
    isLoading,
    error,
    setCurrentClientUser,
    setLoading,
    setError,
    reset,
    setSelectedClient,
    clearSelectedClient,
    selectedClient,
    definedSelectedClient,
    definedCurrentClientUser,
  };
});
